// Pure theme-variable diffing. No DOM, no imports — unit-tested via
// node --test and consumed by theme-host.ts under Vite.
// JSDoc types keep TS callers happy.
// KEEP IN SYNC with ./theme-vars.d.mts, which hand-mirrors these
// signatures for the strict shell tsconfig (no allowJs).

/**
 * Vars the SHELL writes inline on <html>, not themes:
 *   --os-screen-w/h  device-host, on every device snapshot
 *   --os-ui-scale    store.ts, when the user drags the scale slider
 * They land on document.documentElement, which is the same element
 * applyThemeVars writes, so without this guard a theme naming one of them
 * would clobber the live value — and the next theme switch would REMOVE it,
 * which is worse: the phone frame would silently resize to the desktop
 * viewport with nothing left on :root to say why.
 */
export const HOST_OWNED_VARS = new Set(["--os-screen-w", "--os-screen-h", "--os-ui-scale"]);

/**
 * A theme is a package and can ship junk. setProperty does not reject a
 * number or an object — it stringifies, so `--os-danger: 42` becomes the
 * literal "42" and `undefined` becomes "undefined". CSS then falls back
 * silently instead of erroring, and the theme just paints wrong with no
 * trace of where it went wrong. Only strings get through.
 *
 * @param {string} key
 * @param {unknown} value
 * @returns {value is string}
 */
function isApplicable(key, value) {
  if (typeof value !== "string") return false;
  if (!key.startsWith("--os-")) return false;
  return !HOST_OWNED_VARS.has(key);
}

/**
 * Work out what to write onto :root and — the part that was missing — what to
 * take off it.
 *
 * The old applyThemeVars only ever called setProperty, so every variable any
 * theme had ever set stayed on the element forever. Switch from a theme that
 * sets --os-tint-teal to one that does not and the second theme renders with
 * the first one's teal, permanently, because the stale value lives on the
 * element rather than in any theme's map. Reloading does not help; only the
 * removeProperty this diff produces does.
 *
 * `remove` is sorted so two callers holding the same state produce the same
 * sequence — a diff that is order-dependent is a diff nobody can assert on.
 *
 * @param {Iterable<string>|null|undefined} prevKeys keys applied last time
 * @param {Record<string, unknown>|null|undefined} next the incoming var map
 * @returns {{ set: Record<string,string>, remove: string[], keys: string[] }}
 *   `set` to write, `remove` to clear, `keys` to remember for the next call.
 */
export function diffThemeVars(prevKeys, next) {
  /** @type {Record<string,string>} */
  const set = {};
  for (const [k, v] of Object.entries(next || {})) {
    if (isApplicable(k, v)) set[k] = v;
  }

  const remove = [];
  for (const k of prevKeys || []) {
    // A host-owned key can only be here if an older build applied it; it is
    // still not ours to remove — device-host owns the current value.
    if (HOST_OWNED_VARS.has(k)) continue;
    if (!(k in set)) remove.push(k);
  }
  remove.sort();

  return { set, remove, keys: Object.keys(set).sort() };
}
